// Forgot password, reset password and email confirmation pages inside the auth layout.
import { getLanguage, t } from "../core/i18n.js";
import { api } from "../services/storage.js";
import { runtime } from "../core/runtime.js";
import { esc } from "../core/model.js";
import { icon } from "./icons.js";

export const RECOVERY_ROUTES = [
  "/forgot-password",
  "/reset-password",
  "/verify-email",
];

// Read the emailed token once, then drop it from the address bar and history.
let pendingToken = null,
  confirmed = false;
function takeToken() {
  const token = new URLSearchParams(location.search).get("token");
  if (token) {
    pendingToken = token;
    history.replaceState(history.state, "", location.pathname);
  }
  return pendingToken;
}

const header = (eyebrow, title, intro) =>
  `<a class="auth-back" href="/login">← ${t("Back to log in")}</a><div class="auth-eyebrow">${eyebrow}</div><h1>${title}</h1><p class="auth-intro">${intro}</p>`;

function done(wrap, title, text, link, label) {
  wrap.innerHTML = `${header(t("ACCOUNT RECOVERY"), title, text)}<a class="btn black auth-submit auth-done" href="${link}">${label} ${icon("arrow")}</a>`;
}

async function submit(form, error, task) {
  const button = form.querySelector("[type=submit]");
  error.textContent = "";
  button.disabled = true;
  try {
    await task();
  } catch (problem) {
    error.textContent = problem.message;
    button.disabled = false;
  }
}

function forgot(wrap) {
  wrap.innerHTML = `${header(
    t("ACCOUNT RECOVERY"),
    t("Reset your password."),
    t(
      "Enter your account email and we’ll send you a link to set a new password.",
    ),
  )}<form id="recoveryForm" novalidate><label for="recoveryEmail">${t("Email address")}</label><input class="field" id="recoveryEmail" type="email" autocomplete="email" placeholder="you@example.com" required maxlength="254"><p class="auth-error" id="recoveryError" role="alert"></p><button class="btn black auth-submit" type="submit">${t("Send reset link")} ${icon("arrow")}</button></form>`;
  const form = wrap.querySelector("#recoveryForm"),
    error = wrap.querySelector("#recoveryError"),
    email = wrap.querySelector("#recoveryEmail");
  email.focus();
  form.onsubmit = (event) => {
    event.preventDefault();
    if (!email.checkValidity()) {
      error.textContent = t("Enter a valid email address.");
      return;
    }
    submit(form, error, async () => {
      await api("/api/auth/password/forgot", {
        method: "POST",
        data: { email: email.value, language: getLanguage() },
      });
      done(
        wrap,
        t("Check your email."),
        t(
          "If an account exists for {email}, a reset link is on its way. It works for 1 hour.",
          { email: esc(email.value.trim()) },
        ),
        "/login",
        t("Back to log in"),
      );
    });
  };
}

function reset(wrap) {
  const token = takeToken();
  if (!token) {
    done(
      wrap,
      t("This link is incomplete."),
      t("Open the newest link from your email, or request a new one."),
      "/forgot-password",
      t("Request a new link"),
    );
    return;
  }
  wrap.innerHTML = `${header(
    t("ACCOUNT RECOVERY"),
    t("Set a new password."),
    t(
      "Choose a password you haven’t used here before. Other devices will be signed out.",
    ),
  )}<form id="recoveryForm"><div class="auth-password-label"><label for="recoveryPassword">${t("New password")}</label><span id="recoveryHint">${t("8 characters minimum")}</span></div><div class="auth-password-field"><input class="field" id="recoveryPassword" type="password" autocomplete="new-password" required minlength="8" maxlength="256" aria-describedby="recoveryHint"><button type="button" id="recoveryToggle" aria-pressed="false" aria-label="${t("Show password")}">${t("Show")}</button></div><p class="auth-error" id="recoveryError" role="alert"></p><button class="btn black auth-submit" type="submit">${t("Save new password")} ${icon("arrow")}</button></form>`;
  const form = wrap.querySelector("#recoveryForm"),
    error = wrap.querySelector("#recoveryError"),
    password = wrap.querySelector("#recoveryPassword");
  password.focus();
  wrap.querySelector("#recoveryToggle").onclick = (event) => {
    const visible = password.type === "password";
    password.type = visible ? "text" : "password";
    event.currentTarget.textContent = visible ? t("Hide") : t("Show");
    event.currentTarget.setAttribute(
      "aria-label",
      visible ? t("Hide password") : t("Show password"),
    );
    event.currentTarget.setAttribute("aria-pressed", String(visible));
  };
  form.onsubmit = (event) => {
    event.preventDefault();
    if (password.value.length < 8) {
      error.textContent = t("Use a password of 8–256 characters.");
      return;
    }
    submit(form, error, async () => {
      await api("/api/auth/password/reset", {
        method: "POST",
        data: { token, password: password.value },
      });
      pendingToken = null;
      runtime.skipUnloadSave = true;
      location.assign("/today");
    });
  };
}

async function verify(wrap) {
  const token = takeToken();
  wrap.innerHTML = `${header(
    t("EMAIL CONFIRMATION"),
    t("Confirming your email…"),
    "",
  )}`;
  try {
    if (!confirmed) {
      if (!token) throw new Error(t("This link is incomplete."));
      await api("/api/auth/email/verify", { method: "POST", data: { token } });
      pendingToken = null;
      confirmed = true;
    }
    done(
      wrap,
      t("Email confirmed."),
      t("Thank you. Your account is now fully set up."),
      "/today",
      t("Continue to your workspace"),
    );
  } catch (problem) {
    done(
      wrap,
      t("We couldn’t confirm your email."),
      esc(problem.message) +
        " " +
        t("You can send a new link from your account window."),
      "/today",
      t("Continue to your workspace"),
    );
  }
}

export function renderRecovery(host) {
  const wrap = host.querySelector(".auth-form-wrap");
  if (location.pathname === "/forgot-password") forgot(wrap);
  else if (location.pathname === "/reset-password") reset(wrap);
  else verify(wrap);
}
